import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce, useLogger, usePaymentMethods, useSelector } from '~Hooks';

import NumberInput from '~ComponentsImproved/Numpad';
import { useI18n } from '~Hooks';
import { SwipeView } from '../GiftCard/Components/SwipeView';
import { ManualView } from '../GiftCard/Components/ManualView';
import { QRScanView } from '../GiftCard/Components/QRScanView';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';

import useRetain24 from '~Hooks/useRetain24';
import { Subtitle, Tab, Tabs } from '~Components/Microdeb';
import PaymentModal from '~ComponentsImproved/PaymentModal';
import styled from '@emotion/styled';
import { Box, Button, Card } from '~Styles';
import Loader from '~ComponentsImproved/Loader';
import Modal from '~ComponentsImproved/Modal';
import { handyBreakpoint } from '~Styles/breakpoints';

enum TAB_TYPES {
  SWIPE = 'SWIPE',
  MANUAL = 'MANUAL',
  QR = 'QR',
}

type TransactionState =
  | 'LOADING'
  | 'READ_CARD' // Inital state
  | 'INPUT_AMOUNT'
  | 'DISPLAY_BALANCE'
  | 'PAYMENT'
  | 'SUCCESS'
  | 'ERROR'
  | 'DEPOSIT_ERROR'; // Payment went through, but not the deposit API call

interface Props {
  id?: string;
  type: 'PAYMENT' | 'DEPOSIT' | 'REFUND';
  amount?: number;
  onComplete: (transactionId?: string) => void;
  onFail: (reason?: string) => void;
}

const ModalBody = styled(Card)`
  background-color: var(--colorBlueGray1300);
  color: var(--colorBlueGray100);
  display: grid;
  justify-items: center;
  align-items: center;
  min-height: 260px;
  min-width: 300px;
  max-width: 600px;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${handyBreakpoint} {
    padding: 24px 0;
    margin-top: 0;
    margin-bottom: auto;
    align-items: flex-start;
  }
`;

const ProgressButton = styled(Button)`
  justify-self: flex-end;
  color: white;
  background-color: var(--colorBlueGray1200);
`;

const Retain24Payment: React.FunctionComponent<Props> = ({
  id,
  type,
  amount = 0, // This is a fallback
  onComplete,
  onFail,
}) => {
  const [tabType, setTabType] = useState(TAB_TYPES.SWIPE);
  const [transactionState, setTransactionState] = useState<TransactionState>('READ_CARD');
  const [amountToDeposit, setAmountToDeposit] = useState<number>(0);
  const [error, setError] = useState<string | undefined>();
  const [availableBalance, setAvailableBalance] = useState<null | number>(null);
  const retain24api = useRetain24();
  const [cardNumber, setCardNumber] = useState<string>('');
  const { i18n } = useI18n();
  const giftCardsContext = useGiftCardsContext();
  const logger = useLogger('retain-24');
  const paymentMethods = usePaymentMethods();
  const venueId = useSelector((state) => state.venueId);

  // A debounced version of the card number
  const debouncedCardNumber = useDebounce(cardNumber);

  // Filter out the payment methods we do not support for this process
  const allowedPaymentMethods = useCallback(
    () => paymentMethods.all.filter((m) => !['RETAIN24', 'BAEMINGO_GIFTCARD', 'WAITERAID'].includes(m.id)),
    [paymentMethods.all],
  );

  // Delays the exit, so the message can be read
  const startDelayedExit = () => {
    setTimeout(() => {
      // Pass back the id on complete, if defined
      onComplete(id ? id : undefined);
    }, 1600);
  };

  /**
   * When a card number is available, check the available balance on the card
   */
  useEffect(() => {
    if (debouncedCardNumber.length <= 0) {
      return;
    }

    setTransactionState('LOADING');

    retain24api
      .fetchBalance(debouncedCardNumber, venueId || '')
      .then(({ balance }) => {
        if (isNaN(balance)) {
          // This error will be caught in the catch below
          throw new Error('Could not determine balance on card');
        }
        // Baemingo balance is in cents so multiply by 100 to get the correct amount
        setAvailableBalance(balance * 100);
        setTransactionState('DISPLAY_BALANCE');
      })
      .catch((err) => {
        setTransactionState('READ_CARD');
        setError(err.message);
      });
  }, [debouncedCardNumber]);

  function executeTransaction(selectedAmount: number) {
    if (type === 'PAYMENT' && availableBalance! < selectedAmount) {
      setTransactionState('ERROR');
      setError('Not enough balance');
      return;
    }

    setTransactionState('LOADING');

    switch (type) {
      case 'PAYMENT':
        retain24api
          .makePurchase({
            code: debouncedCardNumber,
            // Convert back to without cents
            amount: selectedAmount / 100,
            venueId: venueId || '',
          })
          .then(() => {
            setTransactionState('SUCCESS');
            logger.info('Retain24 payment - Successful', {
              code: debouncedCardNumber,
              amount: selectedAmount / 100,
            });
            startDelayedExit();
          })
          .catch((err) => {
            setError(err.message);
            logger.error('Retain24 payment - FAILED', {
              code: debouncedCardNumber,
              amount: selectedAmount / 100,
            });
            setTransactionState('ERROR');
          });

        break;
      case 'DEPOSIT':
        logger.info('Deposit - Retain24 Deposit initiated.', {
          code: debouncedCardNumber,
          amount: selectedAmount / 100,
        });
        retain24api
          .loadBalance({
            code: debouncedCardNumber,
            amount: selectedAmount / 100,
            venueId: venueId || '',
          })
          .then(() => {
            logger.info('Deposit - Retain24 Deposit successful.', {
              code: debouncedCardNumber,
              amount: selectedAmount / 100,
            });
            setTransactionState('SUCCESS');
            startDelayedExit();
          })
          .catch((err) => {
            logger.error('Deposit - Retain24 Deposit failed. PAYMENT WAS MADE.', {
              err,
            });
            setError(err.message);
            setTransactionState('DEPOSIT_ERROR');
          });

        break;
      case 'REFUND':
        logger.info('Refund - Retain24 Refund initiated.');
        retain24api
          .refundPurchase({
            code: debouncedCardNumber,
            amount: amount! / 100,
            venueId: venueId || '',
          })
          .then(() => {
            logger.info('Refund - Retain24 Refund successful.');
            setTransactionState('SUCCESS');
            startDelayedExit();
          })
          .catch((err) => {
            logger.error('Refund - Retain24 Refund failed.', {
              err,
            });
            setError(err.message);
            setTransactionState('ERROR');
          });

        break;
      default:
        logger.error('Retain24 - Unknown transaction type.', {
          type,
        });
        break;
    }
  }

  const handlePaymentFailure = (e: Error) => {
    setTransactionState('ERROR');
    setError(e.message);
  };

  useEffect(() => {
    if (!giftCardsContext.item) {
      setTransactionState('ERROR');
      setError('Gift card item missing.');
    }
  }, [giftCardsContext.item]);

  const handleTouchOutside = () => {
    // Prevent abort in these states
    if (
      transactionState === 'SUCCESS' ||
      transactionState === 'LOADING' ||
      transactionState === 'PAYMENT'
    ) {
      return;
    }
    onFail('Background pressed');
  };

  return (
    <Modal isShowing={true} hide={handleTouchOutside} zIndex={60}>
      <ModalBody padding="large">
        {transactionState === 'READ_CARD' && (
          <>
            <Tabs>
              <Tab
                onClick={() => setTabType(TAB_TYPES.SWIPE)}
                active={tabType === TAB_TYPES.SWIPE}>
                {i18n('GC.SWIPE')}
              </Tab>
              <Tab
                onClick={() => setTabType(TAB_TYPES.MANUAL)}
                active={tabType === TAB_TYPES.MANUAL}>
                {i18n('GC.MANUAL')}
              </Tab>
              <Tab onClick={() => setTabType(TAB_TYPES.QR)} active={tabType === TAB_TYPES.QR}>
                {i18n('GC.QR')}
              </Tab>
            </Tabs>

            {tabType === TAB_TYPES.SWIPE && (
              <SwipeView cardNumber={cardNumber} setCardNumber={setCardNumber} />
            )}
            {tabType === TAB_TYPES.MANUAL && <ManualView submit={setCardNumber} />}
            {tabType === TAB_TYPES.QR && <QRScanView submit={setCardNumber} />}
            {error && <p>{error}</p>}
          </>
        )}
        {transactionState === 'LOADING' && <Loader showDimmer={false} />}
        {transactionState === 'DISPLAY_BALANCE' &&
        availableBalance !== null && ( // State only reachable when availableBalance is defined, this is for ts
            <>
              {i18n('GC.CardObtained')}
              <Subtitle>
                {i18n('GC.AvailableBalance')}: {(availableBalance! / 100).toFixed(2)}
              </Subtitle>
              <ProgressButton
                fluid
                onClick={(e) => {
                  e.preventDefault();
                  if (type === 'DEPOSIT') {
                    setTransactionState('INPUT_AMOUNT');
                  } else {
                    executeTransaction(amount!);
                  }
                }}>
                {i18n('GC.Continue')}
              </ProgressButton>
            </>
          )}
        {transactionState === 'INPUT_AMOUNT' && (
          <NumberInput
            decimal
            maxCount={5}
            title="Deposit amount"
            onSubmit={(digits: string) => {
              const val = Math.round(parseFloat(digits) * 100);
              setAmountToDeposit(val || 0);
              setTransactionState('PAYMENT');
            }}
            onClose={() => {
              onFail('Cancel');
            }}
          />
        )}
        {transactionState === 'PAYMENT' && (
          <PaymentModal
            onSuccess={() => executeTransaction(amountToDeposit)}
            onError={handlePaymentFailure}
            onAbort={() => setTransactionState('READ_CARD')}
            options={{
              methods: allowedPaymentMethods(),
              disableTip: true,
            }}
            transaction={{ item: giftCardsContext.item!, amount: amountToDeposit || amount! }}
          />
        )}
        {transactionState === 'ERROR' && (
          <>
            {i18n('GC.Wrong')}
            <Subtitle>{error}</Subtitle>
            <ProgressButton fluid onClick={() => onFail(error)}>
              {i18n('GC.OK')}
            </ProgressButton>
          </>
        )}
        {transactionState === 'DEPOSIT_ERROR' && (
          <>
            <Box as="p" marginBottom="large">
              The payment went through, but we could not increase the balance.{' '}
              <b>Use the button below to retry the balance increase.</b> (No additional payment
              required).
              <br />
              <br />
              If this error persists and retrying does not work, please refund the customer and
              contact us.
            </Box>
            <Subtitle>{error}</Subtitle>
            <ProgressButton
              fluid
              marginBottom="medium"
              onClick={() => executeTransaction(amountToDeposit)}>
              {i18n('General.Retry')}
            </ProgressButton>
            <ProgressButton fluid onClick={() => onFail(error)}>
              {i18n('General.Cancel')}
            </ProgressButton>
          </>
        )}
        {transactionState === 'SUCCESS' && <h3>{i18n('General.Success')}</h3>}
      </ModalBody>
    </Modal>
  );
};

export default Retain24Payment;
